import { Autocomplete, FilterOptionsState, TextField } from "@mui/material";
import { useRef } from "react";

interface TabbableAutocompleteProps<T extends { name: string; id: string | number; inputValue?: string }> {
	value: T | null;
	inputValue: string;
	options: T[];
	filterOptions: (options: T[], params: FilterOptionsState<T>) => T[];
	onChange: (_: any, newValue: T | string | null) => void;
	onInputChange: (newValue: string) => void;
	inputRef?: React.RefObject<HTMLInputElement>;
	nextFocusRef?: React.RefObject<HTMLInputElement | HTMLDivElement>;
	disabled?: boolean;
	placeholder?: string;
	freeSolo?: boolean;
}

export const TabbableAutocomplete = <T extends { name: string; id: string | number; inputValue?: string }>({
	value,
	inputValue,
	options,
	filterOptions,
	onChange,
	onInputChange,
	nextFocusRef,
	disabled,
	placeholder = 'Select or type to add',
	freeSolo = false,
	inputRef,
}: TabbableAutocompleteProps<T>) => {
	const valueRef = useRef('');
	const tabPressedRef = useRef(false);

	const sharedProps = {
		value,
		onChange,
		inputValue,
		onInputChange: ((_: any, v: string, reason: string) => {
			if (reason === 'reset' && v === '') return;
			onInputChange(v);
			valueRef.current = v;
		}),
		onBlur: () => {
			if (tabPressedRef.current) {
				tabPressedRef.current = false;
				return;
			}
			const val = valueRef.current;
			if (val && (!value || value.name !== val)) {
				const existing = options.find(
					(o) => o.name.toLowerCase() === val.toLowerCase()
				);
				onChange(null, existing ?? { name: val, id: Date.now(), inputValue: val } as T);
			}
			valueRef.current = '';
		},
		disabled,
		filterOptions,
		renderOption: (props: any, option: T) => (
			<li {...props} key={option.id}>
				{'inputValue' in option ? `Add "${option.name}"` : option.name}
			</li>
		),
		options,
		getOptionLabel: (option: T | string) => {
			if (typeof option === 'string') return option;
			if ('inputValue' in option && option.inputValue) return option.inputValue as string;
			return option.name;
		},
		selectOnFocus: true,
		handleHomeEndKeys: true,
		renderInput: (params: any) => (
			<TextField
				{...params}
				inputRef={inputRef}
				size="small"
				sx={{ padding: "0 !important" }}
				required
				placeholder={placeholder}
				onKeyDown={(e: React.KeyboardEvent) => {
					if(e.key !== 'Tab')
						return;

					tabPressedRef.current = true;
					const inputVal = valueRef.current;
					if (!inputVal) return;

					e.preventDefault();

					const existing = options.find(
						(o) => o.name.toLowerCase() === inputVal.toLowerCase()
					);
					onChange(null, existing ?? { name: inputVal, id: Date.now(), inputValue: inputVal } as T);

					if (!nextFocusRef) {
						setTimeout(() => (document.activeElement as HTMLElement)?.blur(), 0);
						return;
					}

					const focusWhenReady = (attempts = 0) => {
						const el = nextFocusRef.current;
						if (el) {
							const focusable = (el as HTMLElement).querySelector('[role="combobox"]') as HTMLElement;
							(focusable ?? el as HTMLElement).focus();
						} else if (attempts < 20) {
							setTimeout(() => focusWhenReady(attempts + 1), 10);
						}
					};
					setTimeout(() => focusWhenReady(), 0);
				}}
			/>
		),
	};

	return freeSolo ? (
		<Autocomplete<T, false, false, true> {...sharedProps} freeSolo />
	) : (
		<Autocomplete<T> {...sharedProps} />
	);
};